import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Copy, Check, Facebook, MessageCircle, Twitter, Mail, QrCode, Link as LinkIcon, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { BRAND } from "@/lib/brand";

const SITE_URL = "https://securewatch.co.in";
const SHARE_TEXT =
  `🛡️ *SECURE WATCH* — Guwahati's first online remote CCTV monitoring & surveillance service.\n\n` +
  `👁️  Trained live operators watch your home / shop / godown every night (10 PM – 6 AM, or as per your schedule).\n` +
  `🔥 Fire-prevention monitoring · 🚨 SOS medical for senior citizens · 📞 <60 sec alert response.\n\n` +
  `💰 Plans starting *just ₹50 / day*.\n\n` +
  `📞 Call: ${BRAND.phoneDisplay}\n` +
  `📍 Six Mile, Guwahati\n\n` +
  `🌐 ${SITE_URL}`;

export default function ShareDialog({ open, onOpenChange, url = SITE_URL }) {
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);

  const encodedText = encodeURIComponent(SHARE_TEXT);
  const encodedUrl = encodeURIComponent(url);
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=280x280&color=00e5ff&bgcolor=0F0F13&margin=8&data=${encodedUrl}`;

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_TEXT);
      setCopied(true);
      toast.success("Share message copied!");
      setTimeout(() => setCopied(false), 2200);
    } catch { toast.error("Could not copy. Please long-press to copy manually."); }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied!");
    } catch { toast.error("Could not copy the link."); }
  };

  const nativeShare = async () => {
    if (!navigator.share) return copyText();
    try {
      await navigator.share({ title: "Secure Watch", text: SHARE_TEXT, url });
    } catch {/* cancelled */}
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F0F13] border-white/10 text-white sm:max-w-md p-0 overflow-hidden" data-testid="share-dialog">
        {/* Header card */}
        <div className="relative p-6 bg-gradient-to-br from-brand-cyan/15 to-transparent border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-lg bg-white p-1.5">
              <img src={BRAND.logoUrl} alt="Secure Watch" className="h-full w-full object-contain" />
            </div>
            <div>
              <DialogHeader className="p-0 space-y-0">
                <DialogTitle className="font-heading text-xl text-white">Share Secure Watch</DialogTitle>
                <DialogDescription className="text-brand-cyan text-xs uppercase tracking-[0.18em] font-semibold">
                  A Smart Security Solution · Guwahati
                </DialogDescription>
              </DialogHeader>
            </div>
          </div>

          {/* Link row */}
          <div className="mt-5 flex items-center gap-2 rounded-lg border border-white/10 bg-black/50 px-3 py-2">
            <LinkIcon className="h-4 w-4 text-brand-cyan shrink-0" />
            <span className="text-sm text-white/85 truncate flex-1 font-mono">{url}</span>
            <button
              onClick={copyLink}
              data-testid="share-copy-link"
              className="text-xs px-2 py-1 rounded-md bg-brand-cyan/15 border border-brand-cyan/40 text-brand-cyan hover:bg-brand-cyan/25 font-semibold"
            >Copy</button>
          </div>
        </div>

        {/* Share channels grid */}
        <div className="p-6 grid grid-cols-3 sm:grid-cols-4 gap-3">
          <ShareBtn
            testid="share-whatsapp"
            label="WhatsApp"
            color="bg-[#25D366] hover:bg-[#22c55e]"
            href={`https://wa.me/?text=${encodedText}`}
          >
            <svg viewBox="0 0 32 32" className="h-6 w-6 fill-white"><path d="M16.02 4C9.39 4 4 9.39 4 16c0 2.11.56 4.15 1.62 5.94L4 28l6.24-1.62A11.94 11.94 0 0 0 16.02 28C22.65 28 28 22.63 28 16.02 28 9.4 22.65 4 16.02 4z"/></svg>
          </ShareBtn>

          <ShareBtn
            testid="share-facebook"
            label="Facebook"
            color="bg-[#1877F2] hover:bg-[#166fe0]"
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
          >
            <Facebook className="h-6 w-6 fill-white text-white" />
          </ShareBtn>

          <ShareBtn
            testid="share-twitter"
            label="X / Twitter"
            color="bg-black border border-white/20 hover:bg-white/10"
            href={`https://twitter.com/intent/tweet?text=${encodedText}`}
          >
            <Twitter className="h-6 w-6 text-white" />
          </ShareBtn>

          <ShareBtn
            testid="share-telegram"
            label="Telegram"
            color="bg-[#0088CC] hover:bg-[#0077b3]"
            href={`https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`}
          >
            <MessageCircle className="h-6 w-6 text-white" />
          </ShareBtn>

          <ShareBtn
            testid="share-email"
            label="Email"
            color="bg-white/10 border border-white/15 hover:bg-white/15"
            href={`mailto:?subject=${encodeURIComponent("Check out Secure Watch — Guwahati")}&body=${encodedText}`}
          >
            <Mail className="h-6 w-6 text-white" />
          </ShareBtn>

          <ShareBtn
            testid="share-copy-text"
            label={copied ? "Copied!" : "Copy text"}
            color="bg-white/10 border border-white/15 hover:bg-white/15"
            onClick={copyText}
          >
            {copied ? <Check className="h-6 w-6 text-emerald-400" /> : <Copy className="h-6 w-6 text-white" />}
          </ShareBtn>

          <ShareBtn
            testid="share-qr-toggle"
            label={showQR ? "Hide QR" : "QR code"}
            color="bg-white/10 border border-white/15 hover:bg-white/15"
            onClick={() => setShowQR((s) => !s)}
          >
            <QrCode className="h-6 w-6 text-white" />
          </ShareBtn>

          {typeof navigator !== "undefined" && navigator.share && (
            <ShareBtn testid="share-more" label="More…" color="bg-white/10 border border-white/15 hover:bg-white/15" onClick={nativeShare}>
              <ShieldCheck className="h-6 w-6 text-brand-cyan" />
            </ShareBtn>
          )}
        </div>

        {/* QR panel */}
        {showQR && (
          <div className="px-6 pb-6" data-testid="share-qr-panel">
            <div className="rounded-xl border border-brand-cyan/30 bg-black/40 p-4 flex items-center gap-4">
              <img src={qrSrc} alt="QR code for Secure Watch" className="h-32 w-32 rounded-md bg-[#0F0F13]" />
              <div className="text-sm">
                <div className="text-white font-semibold">Scan to open</div>
                <p className="text-white/60 text-xs mt-1 leading-relaxed">
                  Print or show this QR at your office / vehicle wraps / visiting cards. Anyone who scans it
                  lands on your booking page instantly.
                </p>
                <a href={qrSrc} download="secure-watch-qr.png" className="mt-2 inline-block text-xs text-brand-cyan hover:underline">
                  Download PNG →
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Preview text */}
        <div className="border-t border-white/10 px-6 py-4 bg-black/30">
          <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Preview</div>
          <p className="text-xs text-white/70 whitespace-pre-line leading-relaxed line-clamp-4">
            {SHARE_TEXT}
          </p>
          <Button variant="link" className="text-brand-cyan h-auto p-0 text-xs mt-2" onClick={copyText}>
            Copy full message
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ShareBtn({ href, onClick, color, label, testid, children }) {
  const inner = (
    <>
      <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${color}`}>{children}</div>
      <span className="text-[11px] text-white/70 mt-1.5">{label}</span>
    </>
  );
  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer noopener" data-testid={testid} className="flex flex-col items-center hover:-translate-y-0.5 transition-transform">
        {inner}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} data-testid={testid} className="flex flex-col items-center hover:-translate-y-0.5 transition-transform">
      {inner}
    </button>
  );
}
